import { ILibraryFolder, ILibraryListItem } from "@/api";

/**
 * Walks up the parent_id chain from a given folder, returning ancestors
 * ordered root-first. Includes the starting folder as the last element.
 * Guards against circular references.
 */
export function getAncestorChain(
    folders: ILibraryFolder[],
    folder: ILibraryFolder
): ILibraryFolder[] {
    const ancestors: ILibraryFolder[] = [];
    const seen = new Set<number>();
    let cursor: ILibraryFolder | undefined = folder;
    while (cursor) {
        if (seen.has(cursor.id)) break;
        seen.add(cursor.id);
        ancestors.unshift(cursor);
        if (cursor.parent_id == null) break;
        cursor = folders.find((f) => f.id === cursor!.parent_id);
    }
    return ancestors;
}

/**
 * A client-root folder (client_id set, parent_id null) anchors a client
 * workspace: never a movable or deletable subject, though it stays a valid
 * drop DESTINATION (filing into it retags). The single frontend source of
 * truth for the rule -- mirrors the backend `library_service._is_client_root`.
 * Keep the drag / move / row-menu call sites reading it here, not re-inlining
 * the predicate.
 */
export function isClientRootFolder(folder: {
    client_id: number | null;
    parent_id: number | null;
}): boolean {
    return folder.client_id != null && folder.parent_id === null;
}

/**
 * Never a renamable, movable or deletable subject, but still a valid drop
 * DESTINATION: filing into it is how an owner shares a document. The strict
 * `=== true` is load-bearing -- list rows type the flag nullable, and a row
 * without it must keep its actions rather than silently lose them.
 */
export function isSharedRootFolder(folder: {
    is_shared_root?: boolean | null;
}): boolean {
    return folder.is_shared_root === true;
}

export function isForeignSharedSubject(row: {
    shared_by_organization_name: string | null;
}): boolean {
    return row.shared_by_organization_name != null;
}

/**
 * The two structural anchors as one rule. Every surface that takes folders as
 * rename / move / delete SUBJECTS reads this one, so they cannot drift: a
 * protected root drops out of the subjects and the rest of the selection
 * proceeds. Both stay valid DESTINATIONS.
 */
export function isProtectedRootFolder(folder: {
    client_id: number | null;
    parent_id: number | null;
    is_shared_root?: boolean | null;
    shared_by_organization_name: string | null;
}): boolean {
    return (
        isClientRootFolder(folder) ||
        isSharedRootFolder(folder) ||
        isForeignSharedSubject(folder)
    );
}

/**
 * The same rule for a library list row, where only folders can be protected.
 */
export function isProtectedRootListItem(item: ILibraryListItem): boolean {
    return (
        isForeignSharedSubject(item) ||
        (item.type === "folder" && isProtectedRootFolder(item))
    );
}

/**
 * The client subtree: the client root folder plus every descendant, for
 * client-scoped folder pickers (uploads inside a client workspace, where the
 * destination must stay within the client). Returns `[]` when `rootFolderId`
 * is null (no client root yet) or is absent from `folders` (cache not yet
 * refreshed after a first write) — the caller then hides the picker and the
 * upload falls back to the client root. Each entry keeps its `parent_id` /
 * `is_knowledge_base` so downstream `buildFolderOptions` can rebuild the tree.
 */
export function getClientSubtreeFolders(
    folders: ILibraryFolder[],
    rootFolderId: number | null
): ILibraryFolder[] {
    if (rootFolderId == null) return [];
    const root = folders.find((f) => f.id === rootFolderId);
    if (!root) return [];
    const subtree: ILibraryFolder[] = [root];
    const seen = new Set<number>([rootFolderId]);
    const queue = [rootFolderId];
    while (queue.length > 0) {
        const current = queue.pop()!;
        for (const f of folders) {
            if (f.parent_id === current && !seen.has(f.id)) {
                seen.add(f.id);
                subtree.push(f);
                queue.push(f.id);
            }
        }
    }
    return subtree;
}
